import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AsyncSearchSuggestions,
  type SearchSuggestion,
} from '@/components/ui/async-search-suggestions';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTableStateParams } from '@/hooks/useTableStateParams';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight, Loader2, Download, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { usePurchaseOrderStore } from '../store/purchaseOrderStore';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { columns } from '../components/purchase-order-columns';
import type { PoTableMeta } from '../components/purchase-order-columns';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Maps frontend TanStack column IDs to backend sortBy param names */
function toBackendSortBy(columnId: string | undefined): string | undefined {
  if (!columnId) return undefined;
  return columnId;
}

export function PurchaseOrderListPage() {
  usePageTitle('Purchase Orders');
  const navigate = useNavigate();

  const {
    purchaseOrders,
    totalCount,
    totalPages,
    isLoadingPaged,
    pagedError,
    isSyncing,
    lastSyncResult,
    fetchAllPaged,
    syncFromNetSuite,
  } = usePurchaseOrderStore();

  const [syncFromDate, setSyncFromDate] = useState('');

  const getPoSuggestions = useCallback(
    async (query: string): Promise<SearchSuggestion[]> => {
      const results = await purchaseOrderService.search(query);
      return results.map((po) => ({
        id: po.id,
        title: po.poNumber,
        subtitle: `${po.vendorName} · ${po.poStatus}`,
        href: `/mro/purchase-orders/${po.id}`,
      }));
    },
    []
  );

  const { sorting, pagination, onSortingChange, onPaginationChange } =
    useTableStateParams({ defaultPageSize: 10 });

  const fetchPage = useCallback(() => {
    fetchAllPaged({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: toBackendSortBy(sorting[0]?.id),
      sortDesc: sorting[0]?.desc ?? true,
    });
  }, [fetchAllPaged, pagination.pageIndex, pagination.pageSize, sorting]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleSync = async () => {
    const result = await syncFromNetSuite(syncFromDate || undefined);
    if (result) {
      toast.success(
        `Sync complete: ${result.newRecordsCreated} new, ${result.updatedExisting} updated`
      );
      fetchPage();
    } else {
      toast.error('Sync failed');
    }
  };

  const table = useReactTable({
    data: purchaseOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
    autoResetPageIndex: false,
    state: { sorting, pagination },
    onSortingChange,
    onPaginationChange,
    meta: { onRefresh: fetchPage } satisfies PoTableMeta,
  });

  const { pageIndex, pageSize } = pagination;
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Purchase Orders"
        description={
          <>
            Locally synced Purchase Orders from NetSuite.
            {lastSyncResult && (
              <span className="text-muted-foreground ml-2">
                Last sync: {lastSyncResult.newRecordsCreated} new,{' '}
                {lastSyncResult.updatedExisting} updated
                {lastSyncResult.skippedIneligible > 0 && `, ${lastSyncResult.skippedIneligible} skipped`}
                {lastSyncResult.statusChanged > 0 && `, ${lastSyncResult.statusChanged} closed`}
                {lastSyncResult.reopened > 0 && `, ${lastSyncResult.reopened} reopened`}
                {lastSyncResult.flagged > 0 && `, ${lastSyncResult.flagged} flagged`}
              </span>
            )}
          </>
        }
        actions={
          <>
            <span className="text-sm text-muted-foreground">
              {totalCount > 0
                ? `${totalCount.toLocaleString()} PO${totalCount !== 1 ? 's' : ''} total`
                : 'No POs'}
            </span>
            <div className="relative">
              <Input
                type="date"
                value={syncFromDate}
                onChange={(e) => setSyncFromDate(e.target.value)}
                className="w-40"
                placeholder="Sync from date"
                title="Set a custom sync start date (leave empty for auto)"
                disabled={isSyncing}
              />
              {syncFromDate && (
                <button
                  type="button"
                  onClick={() => setSyncFromDate('')}
                  className="absolute right-7 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title="Clear date (use auto sync)"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Button onClick={handleSync} disabled={isSyncing} size="sm">
              {isSyncing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {syncFromDate ? 'Sync From Date' : 'Sync Now'}
            </Button>
          </>
        }
      />

      {pagedError ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-destructive">{pagedError}</p>
          <button
            onClick={fetchPage}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search */}
          <AsyncSearchSuggestions
            getSuggestions={getPoSuggestions}
            placeholder="Search by PO# or vendor name..."
            className="w-80"
            inputClassName="h-9"
          />

          {/* Table — flat: row dividers only, no outer box */}
          <div>
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {isLoadingPaged && purchaseOrders.length === 0 ? (
                      // First/empty load → scoped skeleton rows shaped like the table.
                      // On refetch with existing data we keep the stale rows below.
                      Array.from({ length: pageSize }).map((_, r) => (
                        <TableRow key={`skeleton-${r}`}>
                          {columns.map((_col, c) => (
                            <TableCell key={c}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() =>
                            navigate(`/mro/purchase-orders/${row.original.id}`)
                          }
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center text-muted-foreground"
                        >
                          No purchase orders synced yet. Click "Sync Now" to fetch
                          from NetSuite.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination controls */}
              <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) =>
                      onPaginationChange((prev) => ({ ...prev, pageSize: Number(value), pageIndex: 0 }))
                    }
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    Page {totalPages === 0 ? 0 : pageIndex + 1} of {totalPages}
                  </span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        onPaginationChange((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))
                      }
                      disabled={!canPreviousPage || isLoadingPaged}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        onPaginationChange((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))
                      }
                      disabled={!canNextPage || isLoadingPaged}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
  );
}
